import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, AlertCircle, CheckCircle, Loader2, Trash2, Wrench, ExternalLink, User, AlertTriangle, MoreHorizontal, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import vehicleService from '../../services/vehicleService';
import Button from '../common/Button';

/**
 * AvailabilityModal Component
 * Modal pour gérer les périodes d'indisponibilité d'un véhicule
 * 
 * Features:
 * - Calendrier interactif avec sélection de plage de dates
 * - Visualisation des périodes déjà bloquées
 * - Menu déroulant pour la raison du blocage
 * - Appel API POST /api/vehicles/:id/block-dates
 */

// Options de raison avec icônes
const REASON_OPTIONS = [
  { value: 'maintenance', label: 'Maintenance', description: 'Entretien, vidange, réparation', icon: Wrench, color: 'text-orange-600' },
  { value: 'external_booking', label: 'Réservation externe', description: 'Client direct, téléphone, autre plateforme', icon: ExternalLink, color: 'text-blue-600' },
  { value: 'personal_use', label: 'Usage personnel', description: 'Besoin du propriétaire', icon: User, color: 'text-purple-600' },
  { value: 'accident', label: 'Sinistre', description: 'Accident, expertise assurance', icon: AlertTriangle, color: 'text-red-600' },
  { value: 'other', label: 'Autre', description: 'Autre raison', icon: MoreHorizontal, color: 'text-gray-600' },
];

// Composant Calendrier Interactif
const InteractiveCalendar = ({ startDate, endDate, onDateSelect, blockedDates, reservedDates, currentMonth, onMonthChange }) => {
  const DAYS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
  const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Fonction utilitaire "Local Noon Bypass" pour éviter les problèmes de fuseau horaire
  const getSafeLocalDate = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    // On extrait l'année, le mois et le jour en UTC, et on force 12h00 locale
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0);
  };

  // Formater une date locale en chaîne YYYY-MM-DD (pour éviter toISOString qui utilise UTC)
  const formatLocalDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Lundi = 0
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Vérifier si une date est dans une période bloquée
  const isDateBlocked = (dateStr) => {
    const checkDate = getSafeLocalDate(dateStr);
    if (!checkDate) return false;
    checkDate.setHours(0, 0, 0, 0);
    
    return blockedDates.some((blocked) => {
      const blockedStart = getSafeLocalDate(blocked.startDate);
      const blockedEnd = getSafeLocalDate(blocked.endDate);
      if (!blockedStart || !blockedEnd) return false;
      blockedStart.setHours(0, 0, 0, 0);
      blockedEnd.setHours(0, 0, 0, 0);
      return checkDate >= blockedStart && checkDate <= blockedEnd;
    });
  };

  // Vérifier si une date est dans une période réservée
  const isDateReserved = (dateStr) => {
    const checkDate = getSafeLocalDate(dateStr);
    if (!checkDate) return false;
    checkDate.setHours(0, 0, 0, 0);
    
    return reservedDates.some((reserved) => {
      const reservedStart = getSafeLocalDate(reserved.startDate);
      const reservedEnd = getSafeLocalDate(reserved.endDate);
      if (!reservedStart || !reservedEnd) return false;
      reservedStart.setHours(0, 0, 0, 0);
      reservedEnd.setHours(0, 0, 0, 0);
      return checkDate >= reservedStart && checkDate <= reservedEnd;
    });
  };

  // Générer les jours du mois
  const generateDays = useMemo(() => {
    const days = [];
    const firstDay = getFirstDayOfMonth(currentMonth);
    const daysInMonth = getDaysInMonth(currentMonth);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Jours vides avant le premier jour du mois
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, disabled: true });
    }

    // Jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      // Utiliser formatLocalDateToYYYYMMDD au lieu de toISOString pour éviter les problèmes UTC
      const dateStr = formatLocalDateToYYYYMMDD(date);
      const isPast = date < today;
      const isReserved = isDateReserved(dateStr);
      const isBlocked = isDateBlocked(dateStr);
      const isStart = startDate === dateStr;
      const isEnd = endDate === dateStr;
      const isInRange = startDate && endDate && dateStr > startDate && dateStr < endDate;
      const isToday = date.toDateString() === today.toDateString();

      // Une date est désactivée si elle est passée, bloquée ou réservée
      days.push({
        day,
        date: dateStr,
        disabled: isPast || isBlocked || isReserved,
        isBlocked,
        isReserved,
        isStart,
        isEnd,
        isInRange,
        isToday,
      });
    }

    return days;
  }, [currentMonth, startDate, endDate, blockedDates, reservedDates]);

  const handleDayClick = (dateStr) => {
    if (!dateStr) return;
    onDateSelect(dateStr);
  };

  const goToPrevMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    onMonthChange(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    onMonthChange(newMonth);
  };

  const canGoPrev = currentMonth > new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  return (
    <div className="w-full">
      {/* Header du calendrier */}
      <div className="flex items-center justify-between mb-4 px-2">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className={`p-2 rounded-lg transition ${
            canGoPrev
              ? 'text-gray-700 hover:bg-gray-100'
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <h3 className="text-lg font-semibold text-gray-900">
          {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          onClick={goToNextMonth}
          className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-gray-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Grille du calendrier */}
      <div className="grid grid-cols-7 gap-1">
        {generateDays.map((item, index) => (
          <button
            key={index}
            onClick={() => !item.disabled && handleDayClick(item.date)}
            disabled={item.disabled || !item.day}
            className={`
              relative h-10 rounded-lg text-sm font-medium transition-all duration-150
              ${!item.day ? 'invisible' : ''}
              ${
                item.disabled
                  ? item.isReserved
                    ? 'bg-red-100 text-red-600 cursor-not-allowed'
                    : item.isBlocked
                    ? 'bg-orange-100 text-orange-600 cursor-not-allowed line-through'
                    : 'text-gray-300 cursor-not-allowed'
                  : 'hover:bg-blue-50 text-gray-700 cursor-pointer'
              }
              ${item.isToday && !item.isStart && !item.isEnd && !item.isInRange && !item.isReserved && !item.isBlocked ? 'ring-2 ring-blue-300 ring-inset' : ''}
              ${
                item.isStart
                  ? 'bg-[#1E40AF] text-white hover:bg-[#1E3A8A] rounded-r-none font-semibold'
                  : ''
              }
              ${
                item.isEnd
                  ? 'bg-[#1E40AF] text-white hover:bg-[#1E3A8A] rounded-l-none font-semibold'
                  : ''
              }
              ${item.isInRange ? 'bg-blue-100 text-[#1E40AF] rounded-none' : ''}
              ${item.isStart && item.isEnd ? 'rounded-lg' : ''}
            `}
            title={
              item.isReserved
                ? 'Période réservée par un client'
                : item.isBlocked
                ? 'Période déjà bloquée'
                : item.disabled
                ? 'Date passée'
                : `Sélectionner le ${item.day}`
            }
          >
            {item.day}
          </button>
        ))}
      </div>

      {/* Légende */}
      <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-200 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#1E40AF] rounded"></div>
          <span className="text-gray-600">Sélectionné</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
          <span className="text-gray-600">Réservé</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-orange-100 border border-orange-300 rounded line-through"></div>
          <span className="text-gray-600">Bloqué</span>
        </div>
      </div>
    </div>
  );
};

const AvailabilityModal = ({ isOpen, onClose, vehicleId, vehicleTitle }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [blockedDates, setBlockedDates] = useState([]);
  const [reservedDates, setReservedDates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Load blocked dates when modal opens
  useEffect(() => {
    if (isOpen && vehicleId) {
      loadBlockedDates();
    } else {
      // Reset form when modal closes
      setStartDate('');
      setEndDate('');
      setSelectedReason('');
      setOtherReason('');
      setSuccessMessage('');
      setErrorMessage('');
      setBlockedDates([]);
      setReservedDates([]);
      setCurrentMonth(new Date());
    }
  }, [isOpen, vehicleId]);

  // Fonction utilitaire "Local Noon Bypass" pour éviter les problèmes de fuseau horaire
  const getSafeLocalDate = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    // On extrait l'année, le mois et le jour en UTC, et on force 12h00 locale
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0);
  };

  // Formater une date locale en chaîne YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load blocked dates and reserved dates from API
  const loadBlockedDates = async () => {
    if (!vehicleId) return;

    setIsFetching(true);
    setErrorMessage('');
    try {
      const response = await vehicleService.getBlockedDates(vehicleId);
      
      if (response.success) {
        // Nouvelle structure : response.data contient blockedPeriods et reservedPeriods
        if (response.data && typeof response.data === 'object' && 'blockedPeriods' in response.data) {
          setBlockedDates(response.data.blockedPeriods || []);
          
          // Transformer les dates de réservation avec la méthode "Local Noon Bypass"
          const adjustedReservedDates = (response.data.reservedPeriods || []).map((reserved) => {
            const safeStartDate = getSafeLocalDate(reserved.startDate);
            const safeEndDate = getSafeLocalDate(reserved.endDate);
            return {
              ...reserved,
              startDate: formatDateToYYYYMMDD(safeStartDate),
              endDate: formatDateToYYYYMMDD(safeEndDate),
            };
          });
          
          // Console.log pour vérifier les dates formatées
          console.log('Dates envoyées au calendrier:', adjustedReservedDates);
          
          setReservedDates(adjustedReservedDates);
        } else {
          // Compatibilité avec l'ancienne structure (si le backend n'a pas encore été mis à jour)
          setBlockedDates(Array.isArray(response.data) ? response.data : []);
          setReservedDates([]);
        }
      } else {
        setBlockedDates([]);
        setReservedDates([]);
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
      setErrorMessage('Erreur lors du chargement des dates bloquées et réservées');
      setBlockedDates([]);
      setReservedDates([]);
    } finally {
      setIsFetching(false);
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (dateStr) => {
    if (!startDate || (startDate && endDate)) {
      // Nouvelle sélection
      setStartDate(dateStr);
      setEndDate('');
    } else if (startDate && !endDate) {
      // Sélection de la date de fin
      if (dateStr < startDate) {
        // Si la date sélectionnée est avant la date de début, inverser
        setEndDate(startDate);
        setStartDate(dateStr);
      } else {
        setEndDate(dateStr);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!startDate || !endDate) {
      setErrorMessage('Veuillez sélectionner une plage de dates complète');
      return;
    }

    if (!selectedReason) {
      setErrorMessage('Veuillez sélectionner une raison pour bloquer ces dates');
      return;
    }

    if (selectedReason === 'other' && !otherReason.trim()) {
      setErrorMessage('Veuillez préciser la raison');
      return;
    }

    // Construire la raison finale
    const finalReason = selectedReason === 'other' 
      ? `Autre: ${otherReason.trim()}`
      : REASON_OPTIONS.find(opt => opt.value === selectedReason)?.label || selectedReason;

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Appel API POST /api/vehicles/:id/block-dates
      const response = await vehicleService.blockDates(vehicleId, startDate, endDate, finalReason);

      if (response.success) {
        setSuccessMessage('Dates bloquées avec succès');
        setStartDate('');
        setEndDate('');
        setSelectedReason('');
        setOtherReason('');
        
        // Reload blocked dates
        await loadBlockedDates();

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setErrorMessage(response.message || 'Erreur lors du blocage des dates');
      }
    } catch (error) {
      console.error('Error blocking dates:', error);
      
      // Check if error is about conflicting bookings
      const errorMsg = error.message || '';
      if (errorMsg.includes('réservation') || errorMsg.includes('booking') || errorMsg.includes('disponible') || errorMsg.includes('occupe')) {
        setErrorMessage('Ces dates sont déjà réservées par un client. Impossible de les bloquer.');
      } else {
        setErrorMessage(error.message || 'Erreur lors du blocage des dates');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete blocked date
  const handleDeleteBlockedDate = async (blockedDateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette période bloquée ?')) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Appel API DELETE /api/vehicles/:vehicleId/block-dates/:periodId
      // URL format: `${API_URL}/vehicles/${vehicleId}/block-dates/${periodId}`
      const response = await vehicleService.deleteBlockedDates(vehicleId, blockedDateId);

      if (response.success) {
        // Mise à jour optimiste : retirer immédiatement de la liste locale
        setBlockedDates(prev => prev.filter(blocked => blocked._id !== blockedDateId));
        
        setSuccessMessage('Période bloquée supprimée avec succès');
        
        // Rafraîchir la liste depuis l'API pour s'assurer de la cohérence
        await loadBlockedDates();

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setErrorMessage('Erreur lors de la suppression');
        // En cas d'erreur, recharger pour restaurer l'état
        await loadBlockedDates();
      }
    } catch (error) {
      console.error('Error deleting blocked date:', error);
      setErrorMessage(error.message || 'Erreur lors de la suppression');
      // En cas d'erreur, recharger pour restaurer l'état
      await loadBlockedDates();
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get reason option by value
  const getReasonOption = (reasonValue) => {
    // Si la raison commence par "Autre:", extraire la valeur après "Autre: "
    if (reasonValue?.startsWith('Autre:')) {
      return REASON_OPTIONS.find(opt => opt.value === 'other');
    }
    // Sinon, chercher par label
    return REASON_OPTIONS.find(opt => opt.label === reasonValue || opt.value === reasonValue);
  };

  // Get reason display label
  const getReasonLabel = (reasonValue) => {
    if (reasonValue?.startsWith('Autre:')) {
      return reasonValue;
    }
    const option = getReasonOption(reasonValue);
    return option?.label || reasonValue || 'Non spécifié';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Gestion des disponibilités</h2>
            <p className="text-sm text-gray-600 mt-1">
              {vehicleTitle || 'Véhicule'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 text-sm font-medium">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage('')}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Form to block dates */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bloquer une période</h3>

            {/* Calendrier Interactif */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <InteractiveCalendar
                startDate={startDate}
                endDate={endDate}
                onDateSelect={handleDateSelect}
                blockedDates={blockedDates}
                reservedDates={reservedDates}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
              />
            </div>

            {/* Aperçu de la sélection */}
            {(startDate || endDate) && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-[#1E40AF]" />
                  <span className="font-medium text-gray-900">Période sélectionnée :</span>
                  <span className="text-gray-700">
                    {startDate ? formatDate(startDate) : '...'} → {endDate ? formatDate(endDate) : '...'}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Reason Field - Select */}
              <div>
                <label htmlFor="selectedReason" className="block text-sm font-medium text-gray-700 mb-2">
                  Raison du blocage
                </label>
                <div className="relative">
                  <select
                    id="selectedReason"
                    value={selectedReason}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                      if (e.target.value !== 'other') {
                        setOtherReason('');
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] appearance-none bg-white cursor-pointer pr-10 font-sans"
                  >
                    <option value="">Sélectionnez une raison</option>
                    {REASON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.description}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                
                {/* Champ "Autre" conditionnel */}
                {selectedReason === 'other' && (
                  <div className="mt-3">
                    <label htmlFor="otherReason" className="block text-sm font-medium text-gray-700 mb-2">
                      Précisez la raison
                    </label>
                    <input
                      type="text"
                      id="otherReason"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder="Décrivez la raison..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E] focus:border-[#27489E] font-sans"
                    />
                  </div>
                )}

                {selectedReason && selectedReason !== 'other' && (
                  <p className="mt-1 text-xs text-gray-500 font-sans">
                    {REASON_OPTIONS.find(opt => opt.value === selectedReason)?.description}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting || !startDate || !endDate || !selectedReason || (selectedReason === 'other' && !otherReason.trim())}
                  className="px-6 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                      En cours...
                    </>
                  ) : (
                    'Confirmer le blocage'
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* Blocked Dates List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Périodes bloquées</h3>

            {isFetching ? (
              <div className="text-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#27489E] mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Chargement des périodes bloquées...</p>
              </div>
            ) : blockedDates.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Aucune période bloquée pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blockedDates.map((blocked) => (
                  <div
                    key={blocked._id}
                    className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                        <span className="font-semibold text-gray-900">
                          {formatDate(blocked.startDate)} → {formatDate(blocked.endDate)}
                        </span>
                      </div>
                      {blocked.reason && (
                        <div className="flex items-center gap-2 ml-5 mt-1">
                          {(() => {
                            const reasonOption = getReasonOption(blocked.reason);
                            const Icon = reasonOption?.icon || MoreHorizontal;
                            const iconColor = reasonOption?.color || 'text-gray-600';
                            return (
                              <>
                                <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />
                                <span className="text-sm text-gray-600">{getReasonLabel(blocked.reason)}</span>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteBlockedDate(blocked._id)}
                      disabled={isLoading}
                      className="ml-4 p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Supprimer cette période bloquée"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <Button
            onClick={onClose}
            variant="secondary"
            className="px-6 py-2"
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
